import { useNavigate } from "@solidjs/router";
import type { ParentComponent } from "solid-js";
import { useI18n } from "@/api/i18n";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { usePostHog } from "@/hooks/usePostHog";

type UnauthenticatedApplyModalProps = {
  jobTitle?: string;
  jobId?: string;
};

export const UnauthenticatedApplyModal: ParentComponent<
  UnauthenticatedApplyModalProps
> = (props) => {
  const navigateNext = useNavigate();
  const { t } = useI18n();
  const { capture } = usePostHog();

  const handleLogin = () => {
    // Codificata: la destinazione è un percorso, e concatenarla cruda in una
    // query string è il difetto che `resolveSessionRedirectPath` chiude a valle.
    // Oggi l'id è un UUID e non morde, ma la forma sbagliata sopravvive ai dati
    // che la rendevano innocua.
    navigateNext(
      `/auth?next=${encodeURIComponent(`/job-offers/${props.jobId}/apply`)}`,
    );
  };

  return (
    <Dialog
      onOpenChange={(open) => {
        // L'anonimo su offerta esterna non emette più
        // `click_apply_button_to_external_url` (la modale ha preso il posto di
        // quel bottone): senza questo, il gesto sparirebbe dall'imbuto e un
        // calo di conversione sarebbe indistinguibile da un calo di traffico.
        if (open)
          capture("click_apply_button_unauthenticated", {
            job_offer_id: props.jobId ?? null,
          });
      }}
    >
      <DialogTrigger class="w-full">{props.children}</DialogTrigger>
      <DialogContent class="sm:max-w-md bg-card">
        <DialogHeader>
          <div class="mx-auto mb-4 h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
            <p class="sr-only">Lock Icon</p>
            <i class="ph-fill ph-lock-open text-blue-600 text-2xl" />
          </div>
          <DialogTitle>{t("application.unauthenticated.title")}</DialogTitle>
          <DialogDescription>
            {t("application.unauthenticated.description")}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter class="flex flex-col sm:flex-row gap-2">
          <DialogClose>
            <Button variant="outline" class="w-full sm:w-auto">
              {t("common.cancel")}
            </Button>
          </DialogClose>
          <Button onClick={handleLogin} class="w-full sm:w-auto">
            {t("common.login")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
